import "./MailLog.css"

import React, { useContext, useEffect, useState } from "react"
import { AutoComplete, Modal, Menu, Dropdown, Table, Input, Button, Space, Typography, Tooltip } from "antd"
import { DatePicker } from "antd"
import { SearchOutlined, DownOutlined, SettingOutlined, PushpinOutlined, PushpinFilled, DownloadOutlined } from "@ant-design/icons"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import timezone from "dayjs/plugin/timezone"
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd"

// get global state context
import { GlobalStoreContext } from "../../Store"
import apiConstants from "../../api"
import constants from "../../constants"

dayjs.extend(utc)
dayjs.extend(timezone)

const { RangePicker } = DatePicker
const { Text } = Typography

const MailLog = (props) => {
	// get mutations and state variables from global state
	const { setMenuToolBarTitle, getMailLogRows, getStockistEntries, updateStockist, failMessage, successMessage, authToken } =
		useContext(GlobalStoreContext)

	const [selectedClient, changeClient] = useState(apiConstants.SSE_CLIENTS[0])
	const [rows, setRows] = useState([])
	const [dates, setDates] = useState([
		dayjs().tz("Asia/Kolkata").date(25).subtract(1, "month"),
		dayjs().tz("Asia/Kolkata").date(25),
	])
	const [searchObj, setSearchObj] = useState({})
	const [loading, toggleLoading] = useState(true)
	const [currentPageNo, setPageNo] = useState(1)
	const [currentPageSize, setPageSize] = useState(7)
	const [currentTotal, setCurrentTotal] = useState(0)
	
	// Column management state
	const [columnOrder, setColumnOrder] = useState(() => {
		const saved = localStorage.getItem('mailLogColumnOrder')
		return saved ? JSON.parse(saved) : constants.MAIL_LOG_COLUMNS.map((col) => col.dataIndex)
	})
	const [frozenColumns, setFrozenColumns] = useState(() => {
		const saved = localStorage.getItem('mailLogFrozenColumns')
		return saved ? JSON.parse(saved) : ['action', 'uuid']
	})
	const [columnManageModalVisible, setColumnManageModalVisible] = useState(false)

	// when component mounted
	useEffect(() => {
		// set the menu tool bar title text
		setMenuToolBarTitle("Search Mail Log")
	}, [setMenuToolBarTitle])
	
	// Save column order and frozen columns to localStorage
	useEffect(() => {
		localStorage.setItem('mailLogColumnOrder', JSON.stringify(columnOrder))
	}, [columnOrder])
	
	useEffect(() => {
		localStorage.setItem('mailLogFrozenColumns', JSON.stringify(frozenColumns))
	}, [frozenColumns])

	const handleMenuItemClick = function (e) {
		changeClient(apiConstants.SSE_CLIENTS.find((c) => c.name === e.key.split("_")[0]))
	}

	// watcher
	useEffect(() => {
		toggleLoading(true)
		let mounted = true
		getMailLogRows({
			client: selectedClient,
			filter: searchObj,
			dates,
			pagination: { currentPageNo, currentPageSize },
		})
			.then((response) => {
				if (mounted) {
					setRows(
						response.rows.map((e) => {
							e.hide = e.hide !== undefined ? String(e.hide) : "false"
							e.mapped = e.mapped !== undefined ? String(e.mapped) : "false"
							e.reintroduced = e.reintroduced !== undefined ? String(e.reintroduced) : "false"
							e.date = dayjs(e.date).tz("Asia/Kolkata").format("DD MMMM YYYY HH:mm:ss")
							return e
						})
					)
					setCurrentTotal(response.total)
					toggleLoading(false)
				}
			})
			.catch((e) => {
				if (mounted) {
					failMessage(e.message)
					toggleLoading(false)
				}
			})

		return () => (mounted = false)
		// eslint-disable-next-line
	}, [dates, selectedClient, searchObj, currentPageNo, currentPageSize])

	const menu = (
		<Menu>
			{apiConstants.SSE_CLIENTS.map((e, idx) => {
				return (
					<Menu.Item key={e.name + "_" + idx} onClick={handleMenuItemClick}>
						{e.name}
					</Menu.Item>
				)
			})}
		</Menu>
	)

	const getColumnSearchProps = (dataIndex) => {
		if (dataIndex === "date") return null
		return {
			filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
				<div style={{ padding: 8 }}>
					<Input
						placeholder={`Search ${dataIndex}`}
						value={selectedKeys[0]}
						onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
						onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
						style={{
							width: 188,
							marginBottom: 8,
							display: "block",
						}}
					/>
					<Space>
						<Button
							type="primary"
							onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
							icon={<SearchOutlined />}
							size="small"
							style={{ width: 90 }}
						>
							Search
						</Button>
						<Button onClick={() => handleReset(clearFilters, dataIndex)} size="small" style={{ width: 90 }}>
							Reset
						</Button>
					</Space>
				</div>
			),
			filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />,
			render: (text) => text,
		}
	}
	const handleSearch = (selectedKeys, confirm, dataIndex) => {
		confirm()
		if (selectedKeys[0]) setSearchObj({ ...searchObj, [dataIndex]: selectedKeys[0] })
	}

	const handleReset = (clearFilters, dataIndex) => {
		clearFilters()
		delete searchObj[dataIndex]
		setSearchObj({ ...searchObj })
	}

	const [stkUpdateModalVisible, setStkUpdateModalVisible] = React.useState(false)
	const [confirmModalLoading, setModalLoading] = React.useState(false)
	const [modalSearchValue, setModalSearchValue] = useState("")
	const [selectedMailLogRecord, setSelectedMailLogRecord] = useState({})
	const [selectedStockist, setSelectedStockist] = useState(null)
	const [downloadingUuid, setDownloadingUuid] = useState(null)

	const resetModal = () => {
		setModalSearchValue("")
		setResult([])
		setSelectedStockist(null)
		setSelectedMailLogRecord({})
	}

	const showModal = (record) => {
		resetModal()
		setSelectedMailLogRecord(record)
		setStkUpdateModalVisible(true)
	}

	const handleOk = () => {
		if (selectedStockist === null) {
			return failMessage("Please select a stockist to update")
		}
		setModalLoading(true)
		updateStockist({
			client: selectedClient,
			record: selectedMailLogRecord,
			selectedStockist,
		})
			.then((msg) => successMessage(msg))
			.catch((e) => failMessage(e.message))
			.finally(() => {
				setStkUpdateModalVisible(false)
				setModalLoading(false)
				// trigger table update
				setSearchObj({})
			})
	}

	const handleCancel = () => {
		setStkUpdateModalVisible(false)
		resetModal()
	}

	const [result, setResult] = useState([])
	const handleStockistSearch = (searchText) => {
		getStockistEntries({
			client: selectedClient,
			filter: {
				text: searchText,
			},
		})
			.then((response) => {
				setResult(response)
			})
			.catch((e) => {
				setResult([])
				failMessage(e.message)
			})
	}
	const onChange = (changedValue) => {
		setSelectedStockist(null)
		setModalSearchValue(changedValue)
	}
	const onSelectedStockist = (selectedValue) => {
		setSelectedStockist(selectedValue)
	}
	
	// Download file function
	const handleDownloadFile = async (record) => {
		if (!record.uuid || !record.filename) {
			failMessage('UUID or filename is missing')
			return
		}
		
		setDownloadingUuid(record.uuid)
		
		try {
			const url = `${selectedClient.hostUrl}/utility/v0.1/original-file?uuid=${record.uuid}`
			
			// Show downloading message
			const loadingMessage = `Downloading "${record.filename}"...`
			successMessage(loadingMessage)
			
			const response = await fetch(url, {
				method: 'GET',
				headers: {
					'Authorization': authToken,
				},
			})
			
			if (!response.ok) {
				throw new Error(`Failed to download file: ${response.statusText}`)
			}
			
			const blob = await response.blob()
			
			// Create a temporary anchor element and trigger download
			const downloadUrl = window.URL.createObjectURL(blob)
			const link = document.createElement('a')
			link.href = downloadUrl
			link.download = record.filename
			document.body.appendChild(link)
			link.click()
			document.body.removeChild(link)
			window.URL.revokeObjectURL(downloadUrl)
			
			successMessage(`File "${record.filename}" downloaded successfully`)
		} catch (error) {
			failMessage(`Download failed: ${error.message}`)
		} finally {
			setDownloadingUuid(null)
		}
	}
	
	// Column management functions
	const handleDragEnd = (result) => {
		if (!result.destination) return
		
		const items = Array.from(columnOrder)
		const [reorderedItem] = items.splice(result.source.index, 1)
		items.splice(result.destination.index, 0, reorderedItem)
		
		setColumnOrder(items)
	}
	
	const toggleFreezeColumn = (columnKey) => {
		setFrozenColumns((prev) => {
			if (prev.includes(columnKey)) {
				return prev.filter((key) => key !== columnKey)
			} else {
				return [...prev, columnKey]
			}
		})
	}
	
	const resetColumnSettings = () => {
		const defaultOrder = constants.MAIL_LOG_COLUMNS.map((col) => col.dataIndex)
		setColumnOrder(defaultOrder)
		setFrozenColumns(['action', 'uuid'])
		localStorage.removeItem('mailLogColumnOrder')
		localStorage.removeItem('mailLogFrozenColumns')
		successMessage('Column settings reset to default')
	}
	
	const getColumnByDataIndex = (dataIndex) => {
		return constants.MAIL_LOG_COLUMNS.find((col) => col.dataIndex === dataIndex)
	}
	
	const columns = [
		{
			title: "Action",
			key: "action",
			dataIndex: "action",
			width: 220,
			render: (text, record) => {
				return (
					<div className="action-container">
						<div className="action-button">
							<Button type="primary" onClick={showModal.bind(this, record)} size="small">
								Update Stockist Mapping
							</Button>
						</div>
						<div className="action-button">
							<Button 
								type="default" 
								icon={<DownloadOutlined />} 
								onClick={() => handleDownloadFile(record)} 
								size="small"
								loading={downloadingUuid === record.uuid}
								disabled={downloadingUuid !== null && downloadingUuid !== record.uuid}
							>
								Download File
							</Button>
						</div>
					</div>
				)
			},
		},
	].concat(
		columnOrder
			.map((dataIndex) => {
				const col = getColumnByDataIndex(dataIndex)
				if (!col) return null
				return {
					...col,
					...getColumnSearchProps(col.title),
				}
			})
			.filter(Boolean)
	)
	
	// Apply frozen state to columns
	frozenColumns.forEach((frozenKey) => {
		const colIndex = columns.findIndex((col) => col.key === frozenKey || col.dataIndex === frozenKey)
		if (colIndex !== -1) {
			columns[colIndex].fixed = "left"
		}
	})

	return (
		<div className="mail-log-page">
			<div
				style={{
					display: "flex",
					paddingBottom: "15px",
					justifyContent: "space-between",
				}}
			>
				<Dropdown overlay={menu}>
					{/* eslint-disable-next-line */}
					<a className="ant-dropdown-link" onClick={(e) => e.preventDefault()}>
						Change Client <DownOutlined />
					</a>
				</Dropdown>
				<Typography.Title strong>{selectedClient.name} Mail Log</Typography.Title>
				<div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
					<Button 
						icon={<SettingOutlined />} 
						onClick={() => setColumnManageModalVisible(true)}
						type="default"
					>
						Manage Columns
					</Button>
					<RangePicker
						value={dates}
						onChange={(changedDates) => {
							setDates(changedDates)
						}}
					/>
				</div>
			</div>
			<Space />
			<Table
				bordered={true}
				columns={columns}
				dataSource={rows}
				pagination={{
					position: ["bottomCenter"],
					onChange: (page) => setPageNo(page),
					onShowSizeChange: (current, size) => setPageSize(size),
					current: currentPageNo,
					pageSize: currentPageSize,
					total: currentTotal,
				}}
				loading={loading}
				scroll={{ x: 1500 }}
			/>
			<Modal
				title="Update Stockist Id"
				visible={stkUpdateModalVisible}
				onOk={handleOk}
				confirmLoading={confirmModalLoading}
				onCancel={handleCancel}
			>
				<Text keyboard>{"Currently Mapped Stk Id:"}</Text>
				<Text keyboard>{selectedMailLogRecord.stockistId ? selectedMailLogRecord.stockistId : "N/A"}</Text>
				<br />
				<br />
				<AutoComplete
					style={{
						width: 200,
					}}
					value={modalSearchValue}
					options={result.map((e) => ({ value: e }))}
					onSearch={handleStockistSearch}
					onSelect={onSelectedStockist}
					onChange={onChange}
					placeholder="Search Stockist Id"
				></AutoComplete>
			</Modal>
			
			<Modal
				title="Manage Columns"
				visible={columnManageModalVisible}
				onCancel={() => setColumnManageModalVisible(false)}
				footer={[
					<Button key="reset" onClick={resetColumnSettings}>
						Reset to Default
					</Button>,
					<Button key="close" type="primary" onClick={() => setColumnManageModalVisible(false)}>
						Close
					</Button>,
				]}
				width={600}
			>
				<div style={{ marginBottom: "20px" }}>
					<Text strong>Drag to reorder columns. Click the pin icon to freeze/unfreeze a column.</Text>
				</div>
				<DragDropContext onDragEnd={handleDragEnd}>
					<Droppable droppableId="columns">
						{(provided) => (
							<div {...provided.droppableProps} ref={provided.innerRef}>
								{/* Action column - always first and frozen */}
								<div className="column-item" style={{ opacity: 0.6, cursor: 'not-allowed' }}>
									<div className="column-item-content">
										<span className="drag-handle">☰</span>
										<span className="column-name">Action</span>
										<Tooltip title="This column is always frozen">
											<PushpinFilled className="freeze-icon frozen" />
										</Tooltip>
									</div>
								</div>
								
								{columnOrder.map((dataIndex, index) => {
									const col = getColumnByDataIndex(dataIndex)
									if (!col) return null
									const isFrozen = frozenColumns.includes(dataIndex)
									
									return (
										<Draggable key={dataIndex} draggableId={dataIndex} index={index}>
											{(provided, snapshot) => (
												<div
													ref={provided.innerRef}
													{...provided.draggableProps}
													{...provided.dragHandleProps}
													className={`column-item ${snapshot.isDragging ? 'dragging' : ''}`}
												>
													<div className="column-item-content">
														<span className="drag-handle">☰</span>
														<span className="column-name">{col.title}</span>
														<Tooltip title={isFrozen ? "Click to unfreeze" : "Click to freeze"}>
															{isFrozen ? (
																<PushpinFilled 
																	className="freeze-icon frozen" 
																	onClick={() => toggleFreezeColumn(dataIndex)}
																/>
															) : (
																<PushpinOutlined 
																	className="freeze-icon" 
																	onClick={() => toggleFreezeColumn(dataIndex)}
																/>
															)}
														</Tooltip>
													</div>
												</div>
											)}
										</Draggable>
									)
								})}
								{provided.placeholder}
							</div>
						)}
					</Droppable>
				</DragDropContext>
			</Modal>
		</div>
	)
}
export default MailLog
